import { CheckCircle2, FileText, ShieldCheck, UserCog } from "lucide-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { ComponentSpinner } from "@components/spinner";
import ActiveDebitNotesCard from "./components/active-debit-notes-card";
import AmountsCard from "./components/amounts-card";
import ConfigurationCard from "./components/configuration-card";
import FreshnessBar from "./components/freshness-bar";
import PaymentSetupModal from "./components/payment-setup-modal";
import StatTile from "./components/stat-tile";
import useOverview from "./hooks/use-overview";
import usePaymentSetup from "./hooks/use-payment-setup";

const Overview = ({ brandId, isSelfPayout, onSaved }) => {
    const {
        overview, stats, amounts, activeDebitNotes,
        generatedAt, loading, refreshing, refresh
    } = useOverview(brandId);
    const setup = usePaymentSetup({ brandId, brand: overview, isSelfPayout, onSaved });

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <ComponentSpinner />
            </div>
        );
    }

    const brand = overview || {};

    // Debit notes — the tiles, the debit-note amounts and the active-notes card — only
    // exist under managed payouts; a self-payout brand never has one raised against it.
    return (
        <div className="space-y-4">
            <FreshnessBar generatedAt={generatedAt} refreshing={refreshing} onRefresh={refresh} />

            <div className={classnames("grid grid-cols-1 gap-4 sm:grid-cols-2", { "lg:grid-cols-4": !isSelfPayout })}>
                <StatTile
                    label="Publishers KYC verified"
                    icon={ShieldCheck}
                    value={(
                        <>
                            {stats.verified_affiliates ?? 0}
                            <span className="text-base font-normal text-muted"> / {stats.total_affiliates ?? 0}</span>
                        </>
                    )} />
                <StatTile label="Brand admins" icon={UserCog} value={stats.total_admins ?? 0} hint="With access to this workspace" />
                {!isSelfPayout && (
                    <>
                        <StatTile
                            label="Active debit notes"
                            icon={FileText}
                            value={stats.active_debit_notes ?? 0}
                            hint={`of ${stats.total_debit_notes ?? 0} raised`} />
                        <StatTile
                            label="Settled debit notes"
                            icon={CheckCircle2}
                            value={stats.settled_debit_notes ?? 0}
                            valueClassName="text-success"
                            hint={`of ${stats.total_debit_notes ?? 0} raised`} />
                    </>
                )}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-start">
                <div className="space-y-4 lg:col-span-2">
                    <AmountsCard amounts={amounts} showDebitNotes={!isSelfPayout} />
                    {!isSelfPayout && <ActiveDebitNotesCard notes={activeDebitNotes} total={stats.active_debit_notes} />}
                </div>

                <ConfigurationCard brand={brand} isSelfPayout={isSelfPayout} onEdit={setup.openEditor} />
            </div>

            <PaymentSetupModal setup={setup} brand={brand} />
        </div>
    );
};

Overview.propTypes = {
    brandId     : PropTypes.string.isRequired,
    isSelfPayout: PropTypes.bool,
    onSaved     : PropTypes.func
};

export default Overview;
